<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex align-center justify-space-between">
            <div class="card-header-padding">
              <h5 class="font-weight-bold text-h5 text-typo mb-0">打卡牆</h5>
            </div>
            <v-btn
              v-show="!buttonDisabled"
              elevation="0"
              :ripple="false"
              height="43"
              class="
                font-weight-bold
                text-capitalize
                btn-primary
                bg-gradient-default
                py-3
                px-6
              "
              @click="fetchClockSettings"
              :loading="loading"
            >
              重新整理
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div v-if="loading">讀取中...</div>
            <div v-else>
              <v-row>
                <v-col cols="2">
                  <v-list two-line>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>上班時間</v-list-item-title>
                        <v-list-item-subtitle>{{
                          clockSettings.clock_in_time || "-"
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>下班時間</v-list-item-title>
                        <v-list-item-subtitle>{{
                          clockSettings.clock_out_time || "-"
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>午休</v-list-item-title>
                        <v-list-item-subtitle>
                          <span v-if="clockSettings.lunch_break"
                            >{{ clockSettings.lunch_break_start }} ~
                            {{ clockSettings.lunch_break_end }}</span
                          >
                          <span v-else>-</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>GPS 限制</v-list-item-title>
                        <v-list-item-subtitle>
                          <span v-if="clockSettings.gps_enable"
                            >{{ clockSettings.gps }} ({{
                              clockSettings.gps_scope
                            }}m)</span
                          >
                          <span v-else>未啟用</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>地點</v-list-item-title>
                        <v-list-item-subtitle>{{
                          clockSettings.location || "-"
                        }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="10">
                  <div class="d-flex flex-column align-center">
                    <v-img
                      v-if="qrImageUrl"
                      :src="qrImageUrl"
                      width="50%"
                      :aspect-ratio="1"
                      :max-height="'50vh'"
                      contain
                    />
                    <div v-else class="grey--text">無可用 QR Code</div>
                    <div
                      class="mt-4 d-flex align-center"
                      v-if="!successMessage && clockSettings.qr_dynamic_enable"
                    >
                      <span
                        class="mr-3 text-h4 font-weight-bold"
                        style="color: red"
                        >{{
                          countdown > 0
                            ? `倒數：${Math.floor(countdown / 60)}:${(
                                countdown % 60
                              )
                                .toString()
                                .padStart(2, "0")}`
                            : "失效 請重新整理"
                        }}</span
                      >
                    </div>
                    <div
                      v-if="error"
                      class="red--text mb-4 text-h4 font-weight-bold"
                    >
                      {{ error }}
                    </div>
                    <div
                      v-if="successMessage"
                      class="green--text mb-4 text-h4 font-weight-bold"
                    >
                      {{ successMessage }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import websocketService from "@/utils/websocketService";
import clockSuccessImg from "@/assets/img/clock_success.jpg";
import clockNoImg from "@/assets/img/clock_no.jpg";
export default {
  name: "ClockWall",
  data() {
    return {
      loading: false,
      error: "",
      successMessage: "",
      clockSuccess: false,
      clockSettings: {},
      poller: null,
      lastUpdated: null,
      countdown: 120,
      countdownTimer: null,
      buttonDisabled: true,
      buttonTimer: null,
      websocketRetryTimer: null,
    };
  },
  computed: {
    ...mapGetters({
      websocketConnected: "websocket/isConnected",
    }),
    qrImageUrl() {
      if (this.clockSuccess) {
        return clockSuccessImg;
      }
      if (this.error) {
        return clockNoImg;
      }
      const base = process.env.VUE_APP_API_URL || "";
      const s = this.clockSettings || {};
      const extra = (s && s.extra) || {};
      // Prefer base64 QR when dynamic is enabled and provided
      if (s.qr_dynamic_enable && extra.qr_dynamic_base64) {
        return `data:image/png;base64,${extra.qr_dynamic_base64}`;
      }
      const build = (code) =>
        code ? `${base}/resource/qr/qr_${code}.png` : "";
      if (s.qr_dynamic_enable && s.qr_dynamic) return build(s.qr_dynamic);
      if (s.qr_fixed) return build(s.qr_fixed);
      return "";
    },
  },
  watch: {
    websocketConnected(isConnected) {
      if (isConnected) {
        this.clearWebsocketReconnect();
      } else {
        this.scheduleWebsocketReconnect();
      }
    },
  },
  created() {
    this.fetchClockSettings();
    this.ensureWebsocketConnection();
    websocketService.registerMessageHandler(
      "notification",
      this.handleWebSocketMessage
    );
  },
  beforeDestroy() {
    this.stopCountdown();
    this.stopButtonTimer();
    this.clearWebsocketReconnect();
    websocketService.unregisterMessageHandler(
      "notification",
      this.handleWebSocketMessage
    );
    websocketService.disconnect();
  },
  methods: {
    async ensureWebsocketConnection() {
      try {
        await websocketService.connect();
      } catch (error) {
        console.error("WebSocket connection failed:", error);
        this.scheduleWebsocketReconnect();
      }
    },
    scheduleWebsocketReconnect() {
      if (this.websocketRetryTimer) {
        return;
      }
      this.websocketRetryTimer = setTimeout(() => {
        this.websocketRetryTimer = null;
        this.ensureWebsocketConnection();
      }, 5000);
    },
    clearWebsocketReconnect() {
      if (this.websocketRetryTimer) {
        clearTimeout(this.websocketRetryTimer);
        this.websocketRetryTimer = null;
      }
    },
    async fetchClockSettings() {
      this.loading = true;
      this.error = "";
      this.successMessage = "";
      this.clockSuccess = false;
      try {
        const res = await this.$axios.get("/api/employee-clock-settings/my");
        this.clockSettings = res.data || {};
        this.lastUpdated = new Date();
        // if qr_dynamic_enable is true, start countdown
        if (this.clockSettings.qr_dynamic_enable) {
          this.startCountdown();
        } else {
          this.error = "QR Code 未啟用";
        }
      } catch (e) {
        this.error =
          (e &&
            e.response &&
            e.response.data &&
            (e.response.data.message || e.response.data.error)) ||
          e.message ||
          "取得資料失敗";
      } finally {
        this.loading = false;
        this.startButtonTimer();
      }
    },
    startCountdown() {
      this.stopCountdown();
      this.countdown = 120;
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown -= 1;
        } else {
          this.stopCountdown();
        }
      }, 1000);
    },
    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    resetCountdown() {
      this.startCountdown();
    },
    onRefresh() {
      this.fetchClockSettings();
    },
    formatDate(d) {
      if (!d) return "";
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
      const yyyy = d.getFullYear();
      const MM = pad(d.getMonth() + 1);
      const DD = pad(d.getDate());
      const hh = pad(d.getHours());
      const mm = pad(d.getMinutes());
      const ss = pad(d.getSeconds());
      return `${yyyy}-${MM}-${DD} ${hh}:${mm}:${ss}`;
    },
    startButtonTimer() {
      this.stopButtonTimer();
      this.buttonDisabled = true;
      this.buttonTimer = setTimeout(() => {
        this.buttonDisabled = false;
      }, 30000);
    },
    stopButtonTimer() {
      if (this.buttonTimer) {
        clearTimeout(this.buttonTimer);
        this.buttonTimer = null;
      }
    },
    handleWebSocketMessage(message) {
      console.log(message);
      if (
        message.payload &&
        message.payload.type === "clock_operation" &&
        message.payload.data
      ) {
        this.successMessage = message.payload.message;
        this.clockSuccess = true;
        this.stopCountdown();
      }
    },
  },
};
</script>
